import * as React from 'react';

interface AnimationFrameComponent {
    onAnimationFrame?: (time: number) => void;
}

export interface AnimationFrameComponentProps {
    startAnimation: () => void;
    endAnimation: () => void;
}

/**
 * Manages an animation loop, and calls an onAnimationFrame method
 * on the passed component
 */
export default function withAnimationFrame<TProps>(throttleMs?: number) {
    return (
        InnerComponent: new (
            props: TProps & AnimationFrameComponentProps
        ) => React.Component<TProps & AnimationFrameComponentProps, any> &
            AnimationFrameComponent
    ) => {
        return class AnimatedComponent extends React.Component<
            TProps & AnimationFrameComponentProps
        > {
            private isActive: boolean;
            private rafId: number;
            private lastInvocationMs: number;
            private innerComponent:
                | (React.Component<AnimationFrameComponentProps, any> &
                      AnimationFrameComponent)
                | null;

            constructor(props: TProps & AnimationFrameComponentProps) {
                super(props);

                this.loop = this.loop.bind(this);
                this.endAnimation = this.endAnimation.bind(this);
                this.startAnimation = this.startAnimation.bind(this);

                this.isActive = true;
                this.rafId = 0;
                this.lastInvocationMs = 0;
            }

            public endAnimation() {
                cancelAnimationFrame(this.rafId);

                this.isActive = false;
            }

            public startAnimation() {
                if (!this.isActive) {
                    this.isActive = true;
                    this.rafId = requestAnimationFrame(this.loop);
                }
            }

            public componentDidMount() {
                if (
                    this.innerComponent &&
                    !this.innerComponent.onAnimationFrame
                ) {
                    throw new Error(
                        'The component passed to AnimationFrameComponent does not implement onAnimationFrame'
                    );
                }

                this.rafId = requestAnimationFrame(this.loop);
            }

            public componentWillUnmount() {
                this.endAnimation();
            }

            public render() {
                return (
                    <InnerComponent
                        ref={node => (this.innerComponent = node)}
                        endAnimation={this.endAnimation}
                        startAnimation={this.startAnimation}
                        {...this.props}
                    />
                );
            }

            private loop(time: number) {
                const isAnimatable = !!(
                    this.innerComponent && this.innerComponent.onAnimationFrame
                );

                // Latter const is defensive check for React Native unmount (issues/#3)
                if (!this.isActive || !isAnimatable) {
                    return;
                }

                const hasTimeElapsed =
                    !throttleMs || time - this.lastInvocationMs >= throttleMs;

                if (hasTimeElapsed) {
                    this.lastInvocationMs = time;
                    this.innerComponent &&
                        this.innerComponent.onAnimationFrame &&
                        this.innerComponent.onAnimationFrame(time);
                }

                this.rafId = requestAnimationFrame(this.loop);
            }
        };
    };
}



// WEBPACK FOOTER //
// ./src/client/components/withAnimationFrame.tsx